/** biome-ignore-all lint/nursery/useUniqueElementIds: <explanation> */
/** biome-ignore-all lint/nursery/noSecrets: <explanation> */
import type { FormikProps } from "formik";
import type { Map } from "mapbox-gl";
import type React from "react";
import { type FC, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { SurveyDemographics } from "senf-atomic-design-system";
import type { QuestionValue } from "senf-atomic-design-system/src/components/special/survey/legacy/survey.types";
import { stripMarkdown, useGlobalState } from "senf-shared";
import type { Project, Survey, User } from "senf-shared/types";
import { cn } from "../../../util/classNameConstructor";
import TextToSpeech from "../../atoms/textToSpeech/TextToSpeech";
import Seperator from "../../core/seperator/Seperator";
import { PageFooter } from "../infoPage/InfoPage";
import EditableContent from "./components/EditableContent";
import { SurveyLogos } from "./components/SortableLogos";
import { useDefaultValues, usePrepareSurveyPage } from "./hooks";
import ImageChoice from "./questions/ImageChoice";
import MapInput from "./questions/MapInput";
import Matrix from "./questions/Matrix";
import MultipleChoice from "./questions/MultipleChoice";
import { Outro } from "./questions/Outro";
import ParticipatoryBudget from "./questions/ParticipatoryBudget";
import Ranking from "./questions/Ranking";
import Rating from "./questions/Rating";
import Text from "./questions/Text";
import type { Page } from "./survey.config";
import { useEditQuestion } from "./utils";

export type SurveyQuestionProps<P extends Page> = P & {
  pages: P[];
  setPages?: React.Dispatch<React.SetStateAction<P[]>>;
  featureId?: string;
  setFeatureId?: React.Dispatch<React.SetStateAction<string>>;
  pageIndex: number;
  mode: "edit" | "display";
  preview?: "desktop" | "tablet" | "mobile";
  formik: FormikProps<Survey>;
  demographicsFormik?: FormikProps<User>;
  required?: boolean;
  loadingImage?: boolean;
  isMobile?: boolean;
  statefulMap?: Map;
  user?: User;
  project?: Project;
  surveyId?: string;
  handleSaveDrawn?: (polygon: GeoJSON.Feature) => void;
  setOpenDescription?: (open: boolean) => void;
  showCloseSurveyButton?: boolean;
};

const SurveyQuestion: FC<SurveyQuestionProps<Page>> = (props) => {
  const [accessibility] = useGlobalState().accessibilityState;

  const {
    pageIndex,
    pages,
    loadingImage,
    isMobile,
    mode,
    formik,
    statefulMap,
    demographicsFormik,
    user,
    surveyId,
    project,
    handleSaveDrawn,
  } = props;
  const { t } = useTranslation();

  const {
    currentPage: page,
    currentPageMapData,
    currentPageAlignment,
    currentPageIsMapbased,
    currentPageHasImage,
    currentPageRole,
  } = usePrepareSurveyPage({ pages, index: pageIndex, isMobile, project });
  const pageAlignment =
    !loadingImage || isMobile ? currentPageAlignment : undefined;

  const alignmentClass = {
    left: "items-start justify-start",
    center: "items-center justify-center",
  }[pageAlignment || "left"];

  const handleOnChange = (
    { value, ...rest }: Partial<QuestionValue> = {},
    onLoad = false,
  ) => {
    formik.setFieldValue(page?.uid || "", {
      ...formik.values[page?.uid || ""],
      ...rest,
      value:
        value === undefined
          ? formik.values[page?.uid || ""]?.value || null
          : value,
      timeStamp:
        (!onLoad && new Date().toISOString()) ||
        formik.values[page?.uid || ""]?.timeStamp ||
        null,
    });
  };

  useEffect(() => {
    if (
      currentPageRole === "surveyPage" ||
      currentPageRole === "demographics"
    ) {
      // initiate to set the value of the current page to the formik values
      handleOnChange({}, true);
    }
  }, [pageIndex]);

  const editQuestion = useEditQuestion(props);
  const { updateQuestion, updateOption, addItem, removeItem } = editQuestion;

  const {
    defaultTitle,
    titlePlaceholder,
    defaultDescription,
    defaultTextTitle,
    defaultOtherTitle,
  } = useDefaultValues(page);

  const baseProps = {
    ...formik?.values[page?.uid],
    ...page,
    ...editQuestion,
    page,
    formikValues: formik?.values[page?.uid],
    mode,
    onChange: handleOnChange,
    defaultTextTitle,
    defaultOtherTitle,
    currentPage: page,
    isMobile,
  };

  const [translation] = useGlobalState().translationsState;

  const titleClassName = cn(
    "hyphens-auto break-words",
    isMobile && currentPageRole === "intro" && currentPageHasImage && "px-0",
    isMobile && pageAlignment === "center" && "text-balance",
  );
  const legendClassName = cn(
    isMobile &&
      props.showCloseSurveyButton &&
      !props.preview &&
      currentPageRole !== "intro" &&
      !currentPageHasImage
      ? pageAlignment === "center"
        ? "px-12"
        : "pe-12"
      : "",
  );

  const fieldsetRef = useRef<HTMLDivElement & HTMLFieldSetElement>(null);

  // useEffect(() => {
  //   if (!fieldsetRef.current || mode === "edit") return;

  //   const introFocus = document.getElementById("take-survey-continue-button");

  //   const tabIndexZero = document.querySelectorAll('[tabindex="0"]');
  //   const focused = document.querySelector(":focus");
  //   const focusedVisible = document.querySelector(":focus-visible");

  //   if (pageIndex === 0) {
  //     // for (const element of tabIndexZero) element.setAttribute("tabindex", "2");
  //     // introFocus?.focus()
  //     // introFocus?.setAttribute("tabindex", "1");
  //   } else {
  //     // for (const element of tabIndexZero) element.setAttribute("tabindex", "0");
  //     // introFocus?.removeAttribute("tabindex");
  //   }

  //   const focusable =
  //     pageIndex > 0
  //       ? fieldsetRef.current.querySelector<HTMLElement>(
  //           ':is(button, [href], input, select, textarea, [role="slider"], [tabindex]):not([tabindex="-1"])',
  //         )
  //       : introFocus;
  //   setTimeout(() => {
  //     focusable?.focus();
  //   });

  //   const scrollIntoView = (event) => {
  //     const el = (event.target as HTMLElement)?.closest("label");
  //     if (el) {
  //       el.scrollIntoView({
  //         behavior: "smooth",
  //         block: "nearest",
  //         inline: "center",
  //       });
  //     }
  //   };
  //   fieldsetRef.current?.addEventListener("focusin", scrollIntoView);
  //   return () => {
  //     fieldsetRef.current?.removeEventListener("focusin", scrollIntoView);
  //   };
  // }, [mode, pageIndex, stack.length]);

  // fieldset has quirky display behaviour so in edit mode we use a div instead
  // const sortedOptions = useRandomSorting(page, "options", mode);
  // biome-ignore lint/correctness/useExhaustiveDependencies: we need to update the translations upon page change
  const tts: string = useMemo(() => {
    const base = [
      (page?.title && translation?.[page.title]) || page?.title || defaultTitle,
      (page?.subtitle && translation?.[page?.subtitle]) ||
        page?.subtitle ||
        defaultDescription,
    ];

    // if (currentPageRole === "intro") {
    //   tts.push("Starte die Umfrage mit Klick auf den Button");
    // } else if (currentPageRole === "demographics") {
    //   tts.push(
    //     "Fülle bitte die folgenden Felder aus",
    //     "Postleitzahl, Alter, Geschlecht",
    //   );
    // } else if (currentPageRole === "surveyPage") {
    //   if (page?.type === "text") {
    //     tts.push(
    //       `Schreibe deine Antwort in das ${sortedOptions?.length === 1 ? "das" : `die ${sortedOptions?.length}`} Textfeld.`,
    //     );
    //   } else if (
    //     page?.type === "radio" ||
    //     page?.type === "imageChoice" ||
    //     page?.type === "radioMatrix"
    //   ) {
    //     tts.push(
    //       ...(sortedOptions || []).map(
    //         (option, index) =>
    //           `${index + 1}. option: ${translation?.[option.value] || option.value}`,
    //       ),
    //     );
    //   } else if (
    //     page?.type === "point" ||
    //     page?.type === "path" ||
    //     page?.type === "area"
    //   ) {
    //     tts.push("Wähle eine Option aus.");
    //   } else if (page?.type === "ranking") {
    //     tts.push("Wähle eine Option aus.");
    //   } else if (page?.type === "budget") {
    //     tts.push("Wähle eine Option aus.");
    //   }
    //   if (page?.otherOption) {
    //     tts.push(
    //       `${sortedOptions?.length}. option: ${translation?.[page?.otherTitle] || page?.otherTitle || t("global:other")}`,
    //     );
    //   }
    // }
    return stripMarkdown(base.join(". ").concat("."));
  }, [pageIndex, translation, page, defaultTitle, defaultDescription]);

  if (!page) return null;
  return (
    <div className="flex flex-col gap-2">
      {accessibility && (
        <TextToSpeech
          id="surveyQuestion:ttsPlayClick"
          className={cn("mb-1 -mx-1", alignmentClass)}
          text={tts}
        />
      )}
      <div
        className={cn(
          "flex flex-col w-full p-0",
          isMobile
            ? currentPageIsMapbased
              ? "gap-0 pb-06 max-h-max"
              : "pb-56"
            : "",
          !isMobile ? (currentPageIsMapbased ? "pb-32" : "") : "",
          project &&
            currentPageRole === "intro" &&
            mode === "display" &&
            "pb-20 @xl/page:pb-10",
        )}
        key={pageIndex}
        ref={fieldsetRef}
        // biome-ignore lint/a11y/useSemanticElements: we do not want to use a fieldset here because it messes up the layout
        role="group"
        aria-labelledby={mode === "display" ? "question-legend" : "pageTitle"}
        aria-describedby="question-description"
      >
        {mode === "display" ? (
          <h2
            className={cn(
              "text-2xl @xl/page:text-3xl font-extrabold leading-tighter py-[0.5em]",
              pageAlignment === "center" ? "text-center mx-auto" : "",
              titleClassName,
              legendClassName,
            )}
            id="question-legend"
          >
            {((page.title && translation?.[page.title]) || page.title) ??
              defaultTitle}
          </h2>
        ) : (
          <EditableContent
            id="pageTitle"
            type="textarea"
            key={`pageTitle-${pageIndex}`}
            mode="edit"
            className="heading-3xl leading-9"
            value={(page.title && translation?.[page.title]) || page.title}
            onChange={(e) => updateQuestion(e.target.value, "title")}
            pageAlignment={pageAlignment}
            maxRows={8}
            placeholder={t("global:title")}
            defaultValue={defaultTitle}
          />
        )}
        <EditableContent
          id="pageDescription"
          type="textarea"
          mode={mode}
          rich
          className={cn(titleClassName, "body-md")}
          value={
            page.subtitle != null
              ? translation?.[page.subtitle] || page.subtitle
              : undefined
          }
          onChange={(e) => updateQuestion(e.target.value, "subtitle")}
          pageAlignment={pageAlignment}
          maxRows={18}
          placeholder={t("global:description")}
          defaultValue={
            [
              "choice",
              "choiceImage",
              "choiceMatrix",
              "mapPoint",
              "mapPath",
              "mapArea",
              "ranking",
            ].includes(page.type)
              ? defaultDescription
              : undefined
          }
        />
        {!(
          page.type === "section" ||
          ((page.type === "mapPoint" ||
            page.type === "mapPath" ||
            page.type === "mapArea") &&
            page.settings.valueType === "none")
        ) && (
          <div className={cn("flex flex-col gap-5 my-5")}>
            {(currentPageRole === "intro" || currentPageRole === "outro") &&
            page?.logos?.items?.length ? (
              <SurveyLogos
                page={page}
                setPages={props.setPages}
                formik={formik}
                project={project}
                surveyId={surveyId}
                mode={mode}
                pageAlignment={pageAlignment}
                updateQuestion={updateQuestion}
                removeItem={removeItem}
                addItem={addItem}
                updateOption={updateOption}
              />
            ) : null}

            {currentPageIsMapbased && (
              <MapInput
                {...baseProps}
                formik={formik}
                mapData={currentPageMapData}
                isMobile={isMobile}
                statefulMap={statefulMap}
                featureId={props.featureId}
                setFeatureId={props.setFeatureId}
                setOpenDescription={props?.setOpenDescription}
                handleSaveDrawn={handleSaveDrawn}
              />
            )}
            {page.type === "choiceMatrix" && (
              <Matrix {...baseProps} formik={formik} isMobile={isMobile} />
            )}
            {page.type === "ranking" && <Ranking {...baseProps} />}

            {!currentPageIsMapbased && page.type === "choice" && (
              <MultipleChoice {...baseProps} />
            )}
            {!currentPageIsMapbased && page.type === "budgetAllocation" && (
              <ParticipatoryBudget {...baseProps} />
            )}

            {!currentPageIsMapbased && page.type === "choiceImage" && (
              <ImageChoice
                {...baseProps}
                project={props.project}
                surveyId={props.surveyId}
                updateOption={updateOption}
              />
            )}
            {page.type === "demographics" && (
              <SurveyDemographics
                {...baseProps}
                demographicsFormik={demographicsFormik}
                project={props.project}
                user={user}
              />
            )}
            {!currentPageIsMapbased &&
              (page.type === "ratingScale" ||
                page.type === "ratingStars" ||
                page.type === "ratingSlider") && <Rating {...baseProps} />}

            {page.type === "textResponse" && (
              <Text
                {...baseProps}
                onChange={(value) => handleOnChange({ value })}
                defaultTextTitle={defaultTextTitle}
              />
            )}
            {page.type === "numberInput" && (
              <Text
                {...baseProps}
                onChange={(value) => handleOnChange({ value })}
                defaultTextTitle={defaultTextTitle}
              />
            )}
            {currentPageRole === "outro" && (
              <Outro
                page={page}
                pageAlignment={pageAlignment}
                className={alignmentClass}
                project={project}
                surveyId={surveyId}
                mode={mode}
              />
            )}

            {project && currentPageRole === "intro" && mode === "display" && (
              <div
                className={cn(
                  !isMobile &&
                    "fixed bottom-0 flex justify-center items-end pointer-events-none ease-expressive start-[calc((100vw-var(--w-screen,100vw))/2)]",
                  !isMobile &&
                    currentPageHasImage &&
                    "start-[calc(var(--w-screen,100vw)/2)] w-[calc(var(--w-screen,100vw)/2)] transition-[top,bottom,height,width]",

                  !isMobile &&
                    !currentPageHasImage &&
                    "start-0 w-[var(--w-screen,100vw)] transition-[inset,height,width]",
                )}
              >
                <div
                  className={cn(
                    !isMobile &&
                      "flex mb-5 flex-wrap items-center h-11 w-full mx-auto px-10 group-[.has-image:not(.fill)]:ps-5",
                    !isMobile &&
                      currentPageHasImage &&
                      "max-w-[720px] mb-20 @xl/page:mb-5",
                    // !isMobile && pageAlignment && "ms-10",
                  )}
                >
                  {isMobile && <Seperator className="p-10" />}
                  <footer
                    className={cn(
                      "flex gap-3 pointer-events-auto bg-white -mx-5 -my-3 px-5 py-3 rounded-full",
                      isMobile && "justify-center pb-32",
                    )}
                  >
                    <PageFooter
                      project={project}
                      className={cn(isMobile && "justify-center")}
                    />
                  </footer>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveyQuestion;
